import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabaseService } from '../../lib/supabase';
import { resizeImage } from '../../lib/utils';
import { User } from '../../types/supabase';
import { logger } from '../../utils/logger';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { useAuthUIStore } from '../../store/authUIStore';
import {
  AvatarUploadResponse,
  GoogleSignInOptions,
  ProfileUpdateInput,
  SignInInput,
  SignUpInput,
} from './authTypes';
import { authKeys } from './authKeys';
import { ZentimeDatabase } from '../../lib/offline-db';
import { ALLOWED_IMAGE_TYPES } from '../../constants/app';

/**
 * Hook for accessing the current user
 * Used for reading the current authentication state
 */
export function useUser() {
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: authKeys.user(),
    queryFn: async (): Promise<User | null> => {
      try {
        // Check for local token first to avoid unnecessary requests and errors
        const hasLocalTokens =
          localStorage.getItem('sb-refreshToken') ||
          localStorage.getItem('sb-accessToken') ||
          localStorage.getItem('supabase.auth.token');

        // If we know we're logged out, just return null without hitting Supabase
        if (!hasLocalTokens) {
          logger.store('No local auth tokens found, skipping user fetch');
          return null;
        }

        const supabase = await supabaseService.getClient();

        // First check for session to avoid AuthSessionMissingError
        const { data: sessionData } = await supabase.auth.getSession();

        // If no session exists, just return null (logged out state)
        if (!sessionData?.session) {
          logger.store('No session found, returning null for user');
          return null;
        }

        // If we have a session, get the user data
        const {
          data: { user },
          error,
        } = await supabase.auth.getUser();

        if (error) throw error;

        // Set user info in Sentry for error tracking if user exists
        if (user) {
          logger.setUser({
            id: user.id,
            email: user.email || undefined,
            username: user.user_metadata?.display_name || undefined,
          });
        }

        return user;
      } catch (error) {
        // Special handling for AuthSessionMissingError
        if (error instanceof Error && error.name === 'AuthSessionMissingError') {
          logger.store('Auth session missing, returning null for user');
          // Clear any leftover tokens that might be invalid
          localStorage.removeItem('sb-refreshToken');
          localStorage.removeItem('sb-accessToken');
          localStorage.removeItem('supabase.auth.token');
          return null;
        }

        const appError = handleError(error, {
          action: 'silent',
          context: { operation: 'getUser' },
        });
        throw appError;
      }
    },
    // Don't refetch on window focus since auth should be handled by onAuthStateChange
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for checking admin status
 * Determines if the current user has admin privileges
 */
export function useAdminStatus() {
  const { data: user } = useUser();
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: authKeys.adminStatus(),
    queryFn: async (): Promise<boolean> => {
      if (!user) {
        logger.store('checkAdminStatus: No user available');
        return false;
      }

      try {
        // Always verify with server for security
        const supabase = await supabaseService.getClient();

        const { data, error } = await supabase.rpc('is_admin', {
          user_id: user.id,
        });

        if (error) {
          logger.error('checkAdminStatus: RPC error', {
            error: error.message,
            code: error.code,
          });
          return false;
        }

        // Return the result directly from the server
        return data === true || Boolean(data);
      } catch (error) {
        const appError = handleError(error, {
          action: 'silent',
          context: { operation: 'checkAdminStatus', userId: user?.id },
        });

        logger.error('checkAdminStatus: Exception', {
          error: appError instanceof Error ? appError.message : String(appError),
        });

        return false;
      }
    },
    enabled: !!user,
    // Cache admin status for 30 minutes
    staleTime: 30 * 60 * 1000,
    // Don't refetch on window focus for better performance
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for checking if the user needs to complete profile setup
 * Determines if the user has set a display name
 */
export function useNeedsProfileSetup() {
  const { data: user } = useUser();

  // Calculate if profile setup is needed based on user metadata
  const needsSetup =
    !user ||
    !user.user_metadata ||
    !user.user_metadata.display_name ||
    user.user_metadata.display_name.trim() === '';

  return { needsProfileSetup: needsSetup };
}

/**
 * Hook for signing in with email and password
 */
export function useSignIn() {
  const queryClient = useQueryClient();
  const { handleError } = useQueryErrorHandler();
  const { setInitialized } = useAuthUIStore();

  return useMutation({
    mutationFn: async (input: SignInInput | [string, string]): Promise<User> => {
      // Extract email and password from either object or array pattern
      let email: string;
      let password: string;

      if (Array.isArray(input)) {
        // Handle array format: [email, password]
        [email, password] = input;
      } else {
        // Handle object format: { email, password }
        ({ email, password } = input);
      }

      // Validate inputs
      if (!email) {
        throw new Error('Email is required');
      }

      if (!password) {
        throw new Error('Password is required');
      }

      try {
        const supabase = await supabaseService.getClient();
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        if (!data.user) {
          throw new Error('Sign in succeeded but no user was returned');
        }

        console.log('User signed in:', data.user);
        return data.user;
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'signIn' },
        });
        throw appError;
      }
    },
    onSuccess: user => {
      // Update the user in the cache
      queryClient.setQueryData(authKeys.user(), user);

      // Set initialization state
      setInitialized(true);
    },
  });
}

/**
 * Hook for signing up with email and password
 */
export function useSignUp() {
  const queryClient = useQueryClient();
  const { handleError } = useQueryErrorHandler();
  const { setInitialized } = useAuthUIStore();

  return useMutation({
    mutationFn: async (input: SignUpInput | [string, string]): Promise<User> => {
      // Extract email and password from either object or array pattern
      let email: string;
      let password: string;

      if (Array.isArray(input)) {
        // Handle array format: [email, password]
        [email, password] = input;
      } else {
        // Handle object format: { email, password }
        ({ email, password } = input);
      }

      // Validate inputs
      if (!email) {
        throw new Error('Email is required');
      }

      if (!password) {
        throw new Error('Password is required');
      }

      try {
        logger.store('Starting signup process', { email });

        const supabase = await supabaseService.getClient();

        // Log the API keys being used (exclude the actual values for security)
        const serviceUrl = supabase.supabaseUrl;
        logger.store('Supabase client initialized', {
          url: serviceUrl,
          hasAnonKey: Boolean(supabase.supabaseKey),
          keyLength: supabase.supabaseKey ? supabase.supabaseKey.length : 0,
        });

        const signupOpts = {
          email,
          password,
          options: {
            data: {
              role: 'user',
              display_name: '',
              avatar_url: null,
              updated_at: new Date().toISOString(),
              locale: 'en-GB', // Default to en-GB (Monday first)
            },
          },
        };

        logger.store('Sending signup request with options', {
          hasEmail: Boolean(email),
          hasPassword: Boolean(password),
          userData: signupOpts.options.data,
        });

        const { data, error } = await supabase.auth.signUp(signupOpts);

        if (error) {
          logger.error('Signup request returned error', {
            message: error.message,
            status: error.status,
            name: error.name,
            code: error.code,
          });
          throw error;
        }

        if (!data.user) {
          logger.store('Signup returned no user data', { data });
          throw new Error('Sign up succeeded but no user was returned');
        }

        const hasSession = Boolean(data.session);
        logger.store('User signed up successfully', {
          userId: data.user.id,
          email: data.user.email,
          emailConfirmed: data.user.email_confirmed_at !== null,
          hasSession,
        });

        return data.user;
      } catch (error) {
        logger.error('Signup process exception', {
          errorName: error.name,
          errorMessage: error.message,
          stack: error.stack,
        });

        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'signUp' },
        });

        throw appError;
      }
    },
    onSuccess: user => {
      // Update the user in the cache
      queryClient.setQueryData(authKeys.user(), user);

      // Set initialization state
      setInitialized(true);
    },
  });
}

/**
 * Hook for signing in with Google OAuth
 */
export function useSignInWithGoogle() {
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ additionalParams }: GoogleSignInOptions = {}): Promise<void> => {
      try {
        const supabase = await supabaseService.getClient();

        // Build the redirect URL with any additional params
        let redirectUrl = `${window.location.origin}/auth/callback`;

        // Add default locale preference to the OAuth flow
        // This will be available in user_metadata after authentication
        if (!additionalParams) {
          additionalParams = {};
        }
        if (!('locale' in additionalParams)) {
          additionalParams.locale = 'en-GB'; // Default to Monday first
        }

        // If we have additional params, add them to the redirect URL
        if (Object.keys(additionalParams).length > 0) {
          const params = new URLSearchParams();
          for (const [key, value] of Object.entries(additionalParams)) {
            params.append(key, value);
          }
          redirectUrl += `?${params.toString()}`;
          logger.store('Google auth with additional params', { redirectUrl, additionalParams });
        }

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: redirectUrl,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            },
          },
        });

        if (error) throw error;

        // The user will be redirected to Google's OAuth flow
        // and then back to the redirectUrl specified above
        logger.store('Google auth initiated:', data);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'signInWithGoogle' },
        });

        logger.error('Google Sign-in Error:', appError);
        throw appError;
      }
    },
  });
}

/**
 * Hook for signing out
 */
export function useSignOut() {
  const queryClient = useQueryClient();
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      try {
        const supabase = await supabaseService.getClient();

        // Get the current session first to see if we're authenticated
        const {
          data: { session },
        } = await supabase.auth.getSession();

        // If session exists, perform normal sign out
        if (session) {
          const { error } = await supabase.auth.signOut();
          if (error) {
            logger.error('Error signing out with session:', error);
            throw error;
          }
        } else {
          // If no session exists, just clear local storage and state
          logger.store('No active session found for sign out, clearing auth state');

          // Clear any localStorage tokens just in case
          localStorage.removeItem('supabase.auth.token');
          localStorage.removeItem('sb-refreshToken');
          localStorage.removeItem('sb-accessToken');
        }

        // Clear user from Sentry tracking
        logger.clearUser();
      } catch (error) {
        logger.error('Error during sign out:', error);

        // Clear user from Sentry tracking
        logger.clearUser();

        // Handle error but with a direct flag to prevent circular dependency
        if (!(error instanceof Error && error.name === 'AuthSessionMissingError')) {
          // Use the operation marker to prevent circular dependency in error handler
          handleError(error, {
            action: 'toast',
            context: {
              operation: 'signOut',
              preventRecursion: true,
            },
          });
          throw error;
        }
      }
    },
    onSuccess: async () => {
      logger.store('Sign out successful, clearing all cached data');

      // 1. Clear React Query cache completely
      queryClient.clear();

      // 2. Clear localStorage persistence for React Query
      // The default key is REACT_QUERY_OFFLINE_CACHE
      localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
      logger.store('Removed React Query persisted cache');

      // Also clear any other tanstack-query keys just in case
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('tanstack-query') || key.includes('REACT_QUERY')) {
          localStorage.removeItem(key);
          logger.store(`Removed persisted cache key: ${key}`);
        }
      });

      // 3. Clear IndexedDB
      try {
        const db = new ZentimeDatabase();
        await db.delete();
        logger.store('Cleared IndexedDB offline database');
      } catch (error) {
        logger.error('Error clearing IndexedDB:', error);
      }

      // 4. Clear any other auth-related localStorage items
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('sb-refreshToken');
      localStorage.removeItem('sb-accessToken');
      localStorage.removeItem('lastRoute');

      // 5. Force navigation to landing page to ensure clean transition
      window.location.href = '/';
    },
  });
}

/**
 * Hook for uploading a user's avatar
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (file: File): Promise<AvatarUploadResponse> => {
      if (!user) throw new Error('No user logged in');

      // Validate file type - only allow supported image formats (from centralized constants)
      if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
        const error = new Error(
          `Unsupported image format: ${file.type}. Please use JPEG, PNG, GIF, or WebP.`
        );
        handleError(error, {
          action: 'toast',
          customMessage: 'Please upload a JPEG, PNG, GIF, or WebP image. SVG is not supported.',
          context: { operation: 'uploadUserAvatar', fileType: file.type },
        });
        throw error;
      }

      try {
        // Resize the image first
        const resizedBlob = await resizeImage(file, 1000);
        const resizedFile = new File([resizedBlob], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });

        const fileName = `${user.id}/avatar.${file.type.split('/')[1]}`;
        const supabase = await supabaseService.getClient();

        // Remove existing avatar
        try {
          const { error: removeError } = await supabase.storage.from('avatars').remove([fileName]);

          if (removeError) {
            console.warn('Error removing existing avatar:', removeError);
          }
        } catch (removeError) {
          console.warn('Error in remove operation:', removeError);
        }

        // Upload new avatar
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(fileName, resizedFile, {
            upsert: true,
            contentType: file.type,
            cacheControl: '3600',
          });

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw uploadError;
        }

        // Get public URL
        const {
          data: { publicUrl },
        } = supabase.storage.from('avatars').getPublicUrl(fileName);

        if (!publicUrl) {
          throw new Error('Failed to get public URL for uploaded avatar');
        }

        // Update user profile with new avatar URL
        const { data, error } = await supabase.auth.updateUser({
          data: {
            ...user.user_metadata,
            avatar_url: publicUrl,
            updated_at: new Date().toISOString(),
          },
        });

        if (error) throw error;

        if (!data.user) {
          throw new Error('Avatar update succeeded but no user was returned');
        }

        return { userId: user.id, publicUrl };
      } catch (error) {
        console.error('Error uploading user avatar:', error);

        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'uploadAvatar' },
        });

        throw new Error(appError instanceof Error ? appError.message : 'Failed to upload avatar');
      }
    },
    onSuccess: result => {
      // Update the user in the cache with the new avatar URL
      queryClient.setQueryData<User | null>(authKeys.user(), oldUser => {
        if (!oldUser) return null;

        return {
          ...oldUser,
          user_metadata: {
            ...oldUser.user_metadata,
            avatar_url: result.publicUrl,
            updated_at: new Date().toISOString(),
          },
        };
      });
    },
  });
}

/**
 * Hook for updating user profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (updates: ProfileUpdateInput): Promise<User> => {
      if (!user) throw new Error('No user logged in');

      try {
        const supabase = await supabaseService.getClient();
        const { data, error } = await supabase.auth.updateUser({
          data: {
            ...user.user_metadata,
            ...updates,
            updated_at: new Date().toISOString(),
          },
        });

        if (error) throw error;

        if (!data.user) {
          throw new Error('Profile update succeeded but no user was returned');
        }

        return data.user;
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'updateProfile' },
        });

        throw appError;
      }
    },
    onSuccess: updatedUser => {
      // Update the user in the cache
      queryClient.setQueryData(authKeys.user(), updatedUser);
    },
  });
}
